<script setup lang="ts">
import { computed, ref, watch } from "vue"
import { useRoute } from "vue-router"
import { useI18n } from "vue-i18n"
import { useBuildingStore } from "@core/stores/building-v2.store"
import { useConfigStore } from "@core/stores/config.store"
import { ALARME_ALLOWED_BUILDING } from "@modules/energy-manager/constants"
import { buildAlarmThresholdVariablesOptions } from "@modules/energy-manager/utils/alarmThresholdVariablesOptions"
import { shouldIncludeAlarmVariableForBuilding } from "@modules/energy-manager/utils/alarmMonitoringVariables"

const route = useRoute()
const buildingStore = useBuildingStore()
const configStore = useConfigStore()
const isAddingThreshold = ref(false)
/** Ouverture de la modale de génération groupée de seuils. */
const isBulkGenerateDialogVisible = ref(false)
const { t } = useI18n({
    messages: {
        fr: {
            alarm: {
                addAlarm: "Ajouter une alarme",
                bulkGenerate: "Générer des alarmes",
                bulkGenerateDisabledTooltip:
                    "Toutes les alarmes possibles pour chaque type de données sont déjà créées. Supprimez-en pour pouvoir en générer de nouveau.",
                emptyAlarmsHint:
                    "Vous n'avez pas encore d'alarme. Utilisez le bouton « Ajouter une alarme » pour en créer une.",
            },
        },
    },
})

const getBuildingId = computed(
    () => route.params?._id_building as string | undefined
)
const getBuilding = computed(() => buildingStore.building)
const getBuildingAlarmItem = computed(() => {
    const building = getBuilding.value
    if (!building) return null

    const cleanedAlarme = Object.fromEntries(
        Object.entries(building.alarme ?? {}).map(([variable, config]) => [
            variable,
            Object.fromEntries(
                Object.entries(config).filter(
                    ([, value]) => value !== undefined
                )
            ),
        ])
    )

    return {
        profil: building.profil,
        alarme: cleanedAlarme,
    }
})
const getVariablesOptions = computed(() =>
    buildAlarmThresholdVariablesOptions({
        variables: configStore.getVariables,
        existingProfilKeys: Object.keys(getBuilding.value?.profil ?? {}),
        allowedTypes: ALARME_ALLOWED_BUILDING,
        excludePeriodRaw: true,
        shouldIncludeVariableType: (variableType) =>
            shouldIncludeAlarmVariableForBuilding(
                variableType,
                buildingStore.getSources,
                buildingStore.getMeters
            ),
    })
)

/** True tant qu'aucune variable de profil n'existe : la liste d'alarmes est vide. */
const getHasNoAlarmRows = computed(() => {
    const profil = getBuilding.value?.profil
    return !profil || Object.keys(profil).length === 0
})

/** Génération groupée possible (données chargées et au moins une variable disponible). */
const getCanOpenBulkGenerate = computed(
    () =>
        !configStore.isLoadingVariables &&
        !buildingStore.isFetchingBuilding &&
        !buildingStore.isFetchingBuildingSources &&
        getVariablesOptions.value.length > 0
)

/** Chargement en cours : pas de tooltip sur le bouton désactivé. */
const getIsBulkGenerateContextLoading = computed(
    () =>
        configStore.isLoadingVariables ||
        buildingStore.isFetchingBuilding ||
        buildingStore.isFetchingBuildingSources
)

/** Tooltip sur « Générer des alarmes » uniquement quand désactivé car aucune variable restante. */
const getBulkGenerateDisabledTooltip = computed(() => {
    if (getIsBulkGenerateContextLoading.value || getCanOpenBulkGenerate.value) {
        return null
    }
    return t("alarm.bulkGenerateDisabledTooltip")
})

watch(
    () => route.params?._id_building,
    async (newId) => {
        if (!newId) return
        await configStore.fetchVariables()
    },
    { immediate: true }
)
</script>

<template>
    <div class="page page-building-alarm flex flex-col gap-4">
        <HeaderPageBuilding
            title="Alarmes"
            class-header="flex justify-between items-center flex-wrap gap-4"
        >
            <div class="flex flex-wrap items-center gap-2">
                <span
                    class="inline-flex"
                    v-tooltip.top="
                        getBulkGenerateDisabledTooltip ?? { disabled: true }
                    "
                >
                    <BaseButton
                        severity="secondary"
                        icon="pi pi-bolt"
                        icon-pos="right"
                        :disabled="!getCanOpenBulkGenerate"
                        :label="t('alarm.bulkGenerate')"
                        @click="isBulkGenerateDialogVisible = true"
                    />
                </span>
                <BaseButton
                    @click="isAddingThreshold = !isAddingThreshold"
                    icon="pi pi-plus"
                    icon-pos="right"
                    :label="t('alarm.addAlarm')"
                />
            </div>
        </HeaderPageBuilding>

        <BaseSkeleton
            v-if="
                configStore.isLoadingVariables ||
                buildingStore.isFetchingBuilding ||
                buildingStore.isFetchingBuildingSources
            "
            width="100%"
            height="20rem"
        />
        <template v-else-if="getBuilding && getBuildingId">
            <AlarmBulkGenerateDialog
                v-model:visible="isBulkGenerateDialogVisible"
                type="building"
                :item-id="getBuildingId"
                :variables-options="getVariablesOptions"
            />
            <AlarmFormAdd
                v-if="isAddingThreshold"
                type="building"
                :item-id="getBuildingId"
                :variables-options="getVariablesOptions"
                @cancel="isAddingThreshold = false"
                @save="isAddingThreshold = false"
            />
            <template v-else>
                <BaseMessage v-if="getHasNoAlarmRows" icon="pi pi-info-circle">
                    {{ t("alarm.emptyAlarmsHint") }}
                </BaseMessage>
                <AlarmVariableList
                    v-else
                    type="building"
                    :item-id="getBuildingId"
                    :item="getBuildingAlarmItem"
                />
            </template>
        </template>
    </div>
</template>
